<template>
  <AppContainer :is-loading="isSubmitting">
    <div class="space-y-4">
      <EPageTitleMobile
        :title="t(useTranslationPath(callerPath, 'header'))"
        :description="t(useTranslationPath(callerPath, 'description'))"
      />

      <UserSelection
        v-if="isAddingNewEngineer"
        v-model="newEngineer"
        :label="useTranslationPath(callerPath, 'selectExistingAssistantEngineer')"
        :label-new="useTranslationPath(callerPath, 'newAssistantEngineer')"
        :on-search="fetchEngineers"
      />
      <template v-else>
        <div class="text-surface-foreground font-medium">
          {{ t(useTranslationPath(callerPath, 'mainEngineer')) }}
        </div>
        <EList>
          <EListItem :title="engineerStore.engineerForm.mainEngineer.name">
            <template #action>
              <EButton
                :label="t(useTranslationPath(callerPath, 'editMainEngineer'))"
                variant="secondary"
                icon="pencil"
                icon-only
                @click="editMainEngineer"
              />
            </template>
          </EListItem>
        </EList>

        <template v-if="engineersOnSite.length">
          <div class="text-surface-foreground font-medium">
            {{ t(useTranslationPath(callerPath, 'assistantEngineers')) }}
          </div>
          <EList>
            <EListItem
              v-for="(engineer, index) in engineersOnSite"
              :key="index"
              :title="engineer.name"
            >
              <template #action>
                <EButton
                  :label="t(useTranslationPath(callerPath, 'removeEngineer'))"
                  variant="secondary"
                  icon="trash"
                  icon-only
                  @click="removeEngineer(index)"
                />
              </template>
            </EListItem>
          </EList>
        </template>

        <EButton
          :label="t(useTranslationPath(callerPath, 'addAssistantEngineer'))"
          icon="plus"
          variant="link"
          block
          @click="toggleAddAssistantEngineerForm"
        />
      </template>
    </div>

    <template #bottom>
      <EButton
        :label="t(useTranslationPath(callerPath, 'next'))"
        :disabled="
          isAddingNewEngineer &&
          (newEngineer === null || newEngineer?.length < 3 || isCreatingEngineer || isSubmitting)
        "
        block
        @click="isAddingNewEngineer ? createEngineer() : onSubmit()"
      />
    </template>
  </AppContainer>
</template>

<script setup>
import AppContainer from '@/components/AppContainer.vue'
import UserSelection from '@/components/UserSelection.vue'
import { useGoBack } from '@/composables/useGoBack.js'
import { useTranslationPath } from '@/composables/useTranslationPath.js'
import { isAccessCodeEnabled, isLocationEnabled } from '@/composables/useVerificationMethod.js'
import { useAppStore } from '@/stores/AppStore.js'
import { useEngineerStore } from '@/stores/authenticated/EngineerStore.js'
import { useStartWorkCheckStore } from '@/stores/authenticated/StartWorkCheckStore.js'
import { useWorkOrderStore } from '@/stores/authenticated/WorkOrderStore.js'
import { EButton, EList, EListItem, EPageTitleMobile } from '@expansivefm/eds'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { toast } from 'vue3-toastify'

const { t } = useI18n()

const { setCustomGoBack } = useGoBack()

const router = useRouter()

const appStore = useAppStore()
const engineerStore = useEngineerStore()
const workOrderStore = useWorkOrderStore()
const startWorkCheckStore = useStartWorkCheckStore()

const callerPath = 'views.authenticated.workOrders.engineers.assistants'

const engineers = ref([])
const newEngineer = ref(null)
const isSubmitting = ref(false)
const isAddingNewEngineer = ref(false)
const isCreatingEngineer = ref(false)

const engineersOnSite = computed(() => {
  return engineerStore.engineerForm.engineers
})

onMounted(async () => {
  setCustomGoBack(() => {
    router.push({
      name: 'work-order.engineers.index',
      params: { workOrderId: workOrderStore.workOrder.id },
    })
  })

  appStore.$patch({ showBackButton: true })

  await fetchEngineers()
})

watch(
  () => newEngineer.value,
  async (value) => {
    if (value) {
      if (Number.isInteger(value)) {
        const engineer = engineers.value.find((e) => e.id === value)

        engineerStore.$patch({
          engineerForm: {
            mainEngineer: engineerStore.engineerForm.mainEngineer,
            engineers: [...engineerStore.engineerForm.engineers, engineer],
          },
        })

        isAddingNewEngineer.value = false
        newEngineer.value = null
      }
    }
  },
  { deep: true },
)

const fetchEngineers = async (query) => {
  return await engineerStore.fetchEngineers({ name: query }).then(({ data }) => {
    const users = data.filter(
      (engineer) =>
        engineerStore.engineerForm.mainEngineer?.id !== engineer.id &&
        engineerStore.engineerForm.engineers.every((e) => e.id !== engineer.id),
    )

    engineers.value = users
    return users
  })
}

const createEngineer = async () => {
  isCreatingEngineer.value = true

  await engineerStore
    .createEngineer({ name: newEngineer.value })
    .then(async ({ data }) => {
      engineerStore.$patch({
        engineerForm: {
          mainEngineer: engineerStore.engineerForm.mainEngineer,
          engineers: [...engineerStore.engineerForm.engineers, data],
        },
      })

      isAddingNewEngineer.value = false
      newEngineer.value = null
    })
    .catch(() => {
      toast(t(useTranslationPath(callerPath, 'errors.creatingEngineer')), {
        autoClose: 5000,
        position: toast.POSITION.BOTTOM_CENTER,
        type: 'error',
      })
    })
    .finally(() => (isCreatingEngineer.value = false))
}

const toggleAddAssistantEngineerForm = () => {
  isAddingNewEngineer.value = !isAddingNewEngineer.value
  newEngineer.value = null

  if (isAddingNewEngineer.value) {
    setCustomGoBack(() => {
      isAddingNewEngineer.value = false
      newEngineer.value = null

      setCustomGoBack(() => {
        router.push({
          name: 'work-order.engineers.index',
          params: { workOrderId: workOrderStore.workOrder.id },
        })
      })
    })
  }
}

const editMainEngineer = async () => {
  await router.push({
    name: 'work-order.engineers.index',
    params: { workOrderId: workOrderStore.workOrder.id },
  })
}

const removeEngineer = (index) => {
  engineerStore.engineerForm.engineers.splice(index, 1)
}

const onSubmit = async () => {
  isSubmitting.value = true

  const engineerIds = [{ id: engineerStore.engineerForm.mainEngineer.id }]

  engineerStore.engineerForm.engineers.forEach((engineer) => {
    engineerIds.push({ id: engineer.id })
  })

  let sessionData = {
    workOrder: workOrderStore.workOrder?.id,
    mainEngineer: engineerStore.engineerForm.mainEngineer.id,
    engineers: engineerIds,
    location_verification_method_check_in: null,
  }

  if (
    (isLocationEnabled('ENGINEER_APP_CHECK_IN_METHOD') ||
      isAccessCodeEnabled('ENGINEER_APP_CHECK_IN_METHOD')) &&
    engineerStore.verificationData.checkIn.method !== null
  ) {
    sessionData = {
      ...sessionData,
      location_verification_method_check_in: engineerStore.verificationData.checkIn.method,
    }

    if (engineerStore.verificationData.checkIn.method === 'GEOLOCATION') {
      sessionData = {
        ...sessionData,
        geolocation_check_in: engineerStore.verificationData.checkIn.data,
      }
    }
  }

  await engineerStore
    .createSession(sessionData)
    .then(async ({ data }) => {
      const engineers = engineerStore.engineerForm.engineers.map((engineer) => ({
        id: engineer.id,
        name: engineer.name,
        status: 'active',
      }))

      engineers.push({
        id: engineerStore.engineerForm.mainEngineer.id,
        name: engineerStore.engineerForm.mainEngineer.name,
        status: 'active',
      })

      engineerStore.$patch({ session: data, engineers: engineers })
      startWorkCheckStore.$patch({ hasStartedChecks: false })

      await router.push({
        name: 'work-order.start-work-checks.index',
        params: {
          workOrderId: workOrderStore.workOrder.id,
        },
      })
    })
    .catch(() => {
      toast(t(useTranslationPath(callerPath, 'errors.creatingSession')), {
        autoClose: 5000,
        position: toast.POSITION.BOTTOM_CENTER,
        type: 'error',
      })

      isSubmitting.value = false
    })
}
</script>
